import { needsApi, profileApi, servicesApi } from '../api/endpoints'
import type { LearnerNeedRequest, ProfileUpdateRequest, ProviderServiceRequest } from '../types/api'

/**
 * The bits of a registration that cannot be saved until there is a session.
 *
 * <p>Registration forms collect more than the account itself: a map pin, a bio, the categories
 * somebody wants to learn or teach. All of that goes through authenticated endpoints, and used to
 * be written in the seconds after signing up, because signing up handed back a session.
 *
 * <p>Signing up is two steps now: the form asks for a join code, and nothing exists until that code
 * is entered. So the extras wait here, in this browser, and are written the moment a session first
 * exists, which is straight after the code is accepted.
 *
 * <p>localStorage rather than component state so a reload between the two steps does not lose them.
 *
 * <p>If the stash is missing (storage blocked, a different browser) nothing is written. That is the
 * honest failure: these are all fields the profile page can set afterwards, and a half-filled
 * profile is a much smaller problem than a signup that refuses to complete.
 */
const KEY = 'yellowdot.pendingOnboarding'

export interface PendingOnboarding {
  profile?: ProfileUpdateRequest
  needs?: LearnerNeedRequest[]
  services?: ProviderServiceRequest[]
}

export function savePendingOnboarding(pending: PendingOnboarding) {
  // Nothing worth storing - don't leave an empty object behind for apply() to find.
  if (!pending.profile && !pending.needs?.length && !pending.services?.length) {
    return
  }
  try {
    localStorage.setItem(KEY, JSON.stringify(pending))
  } catch (err) {
    // Private browsing, a full quota, storage switched off. The account is still made; only the
    // extras are lost, and they are all editable on the profile.
    console.error('[onboarding] could not stash the post-registration details', err)
  }
}

export function clearPendingOnboarding() {
  try {
    localStorage.removeItem(KEY)
  } catch {
    // Nothing to do - if it cannot be cleared it will be overwritten by the next registration.
  }
}

/**
 * Writes whatever was stashed, and clears it.
 *
 * <p>Returns false if any part failed, so the caller can say so rather than pretending. Cleared
 * either way: a retry loop over a request the server keeps rejecting would repeat on every page
 * load forever, and the profile page can set all of this by hand.
 */
export async function applyPendingOnboarding(): Promise<boolean> {
  let raw: string | null = null
  try {
    raw = localStorage.getItem(KEY)
  } catch (err) {
    console.error('[onboarding] could not read the stashed details', err)
    return true
  }
  if (!raw) return true

  let pending: PendingOnboarding
  try {
    pending = JSON.parse(raw) as PendingOnboarding
  } catch (err) {
    console.error('[onboarding] the stashed details were unreadable', err)
    clearPendingOnboarding()
    return true
  }

  let allSaved = true
  if (pending.profile) {
    await profileApi.updateOwn(pending.profile).catch((err) => {
      console.error('[onboarding] profile details could not be saved', err)
      allSaved = false
    })
  }
  for (const need of pending.needs ?? []) {
    await needsApi.create(need).catch((err) => {
      console.error(`[onboarding] need "${need.category}" could not be saved`, err)
      allSaved = false
    })
  }
  for (const service of pending.services ?? []) {
    await servicesApi.upsert(service).catch((err) => {
      console.error(`[onboarding] service "${service.category}" could not be saved`, err)
      allSaved = false
    })
  }

  clearPendingOnboarding()
  return allSaved
}
